import api from '../../api/restaurant/restaurantMenuItemApi';
import ProductService from '../ProductService';
import {
  CustomResponse,
  MenuProductOption,
  RestaurantMenuItemDTO,
  RestaurantMenuItemFilters,
  RestaurantMenuItemForm,
  RestaurantMenuItemPage,
} from '../../types/restaurant/RestaurantMenuTypes';
import { RestaurantPageRequest } from '../../types/restaurant/RestaurantRecipeTypes';
import { restaurantPageQuery } from './restaurantPageQuery';
const payload = (form: RestaurantMenuItemForm) => ({
  productId: Number(form.productId),
  categoryId: Number(form.categoryId),
  displayName: form.displayName.trim() || null,
  description: form.description.trim() || null,
  preparationTimeMinutes:
    form.preparationTimeMinutes === ''
      ? null
      : Number(form.preparationTimeMinutes),
  preparationStationId:
    form.preparationStationId === '' ? null : Number(form.preparationStationId),
  available: form.available,
  featured: form.featured,
  displayOrder: Number(form.displayOrder),
  ...(form.id ? { active: form.active } : {}),
});
const RestaurantMenuItemService = {
  listFiltered: async (
    branchId: number,
    request: RestaurantPageRequest<RestaurantMenuItemFilters>
  ) => {
    const filters = request.filters;
    const query = restaurantPageQuery(request.page, request.size, request.sort);

    return (
      await api.addCustom(`/branches/${branchId}/filtered?${query}`, {
        search: filters.search?.trim() || null,
        categoryId: filters.categoryId,
        available: filters.available,
        featured: filters.featured,
        active: filters.active,
      })
    ).data as RestaurantMenuItemPage;
  },
  list: async (branchId: number, activeOnly = false) =>
    (await api.fetchCustom(`/branches/${branchId}?activeOnly=${activeOnly}`))
      .data as CustomResponse<RestaurantMenuItemDTO[]>,
  listByCategory: async (
    branchId: number,
    categoryId: number,
    activeOnly = false
  ) =>
    (
      await api.fetchCustom(
        `/branches/${branchId}/categories/${categoryId}?activeOnly=${activeOnly}`
      )
    ).data as CustomResponse<RestaurantMenuItemDTO[]>,
  listAvailable: async (
    branchId: number,
    filters: {
      categoryId?: number;
      search?: string;
      /** undefined = solo disponibles (default POS); null = todos; true/false = explícito */
      available?: boolean | null;
      featured?: boolean;
      priceMin?: number;
      priceMax?: number;
    } = {}
  ) => {
    const query = new URLSearchParams({ activeOnly: 'true' });
    if (filters.available === undefined) query.set('available', 'true');
    else if (filters.available !== null)
      query.set('available', String(filters.available));
    if (filters.categoryId) query.set('categoryId', String(filters.categoryId));
    if (filters.search?.trim()) query.set('search', filters.search.trim());
    if (filters.featured) query.set('featured', 'true');
    if (filters.priceMin != null && !Number.isNaN(filters.priceMin))
      query.set('priceMin', String(filters.priceMin));
    if (filters.priceMax != null && !Number.isNaN(filters.priceMax))
      query.set('priceMax', String(filters.priceMax));
    return (await api.fetchCustom(`/branches/${branchId}?${query}`))
      .data as CustomResponse<RestaurantMenuItemDTO[]>;
  },
  get: async (id: number) =>
    (await api.fetchById(id)).data as CustomResponse<RestaurantMenuItemDTO>,
  create: async (branchId: number, form: RestaurantMenuItemForm) =>
    (await api.addCustom(`/branches/${branchId}`, payload(form)))
      .data as CustomResponse<RestaurantMenuItemDTO>,
  update: async (id: number, form: RestaurantMenuItemForm) =>
    (await api.modify(id, payload(form)))
      .data as CustomResponse<RestaurantMenuItemDTO>,
  updateAvailable: async (id: number, available: boolean) =>
    (await api.patchCustom(`${id}/available`, { available }))
      .data as CustomResponse<RestaurantMenuItemDTO>,
  updateActive: async (id: number, active: boolean) =>
    (await api.patchCustom(`${id}/active`, { active }))
      .data as CustomResponse<RestaurantMenuItemDTO>,
  updatePreparationTime: async (
    id: number,
    preparationTimeMinutes: number | null
  ) =>
    (await api.patchCustom(`${id}/preparation-time`, { preparationTimeMinutes }))
      .data as CustomResponse<RestaurantMenuItemDTO>,
  products: async () =>
    (await ProductService.getForDropdown()).data as MenuProductOption[],
};
export default RestaurantMenuItemService;
